import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { giftCardsAPI } from '../../api/giftCardsAPI';
import { normalizeGiftStatus } from '../../utils/giftNotificationDisplay';

export const fetchGiftCards = createAsyncThunk(
  'giftCards/fetchGiftCards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.getGiftCards();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch gift cards');
    }
  }
);

export const transferGiftCard = createAsyncThunk(
  'giftCards/transferGiftCard',
  async ({ code, phoneNumber, username }, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.transferGiftCard(code, phoneNumber, username);
      return response;
    } catch (error) {
      const errorData = error.response?.data || {};
      return rejectWithValue({
        error: errorData.error || 'Failed to transfer gift card',
        user_not_found: errorData.user_not_found || false,
        phone_number: errorData.phone_number,
        message: errorData.message || errorData.error,
      });
    }
  }
);

export const redeemGiftCard = createAsyncThunk(
  'giftCards/redeemGiftCard',
  async (code, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.redeemGiftCard(code);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to redeem gift card');
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  'giftCards/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.getNotifications();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'giftCards/markNotificationRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.markNotificationRead(notificationId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as read');
    }
  }
);

export const fetchStatus = createAsyncThunk(
  'giftCards/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await giftCardsAPI.getStatus();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch status');
    }
  }
);

const initialState = {
  giftCards: [],
  notifications: [],
  unreadCount: 0,
  status: null,
  loading: false,           // only reflects gift-card CRUD operations
  notificationsLoading: false,
  statusLoading: false,
  error: null,
};

const giftCardsSlice = createSlice({
  name: 'giftCards',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Gift Cards
      .addCase(fetchGiftCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGiftCards.fulfilled, (state, action) => {
        state.loading = false;
        state.giftCards = action.payload;
      })
      .addCase(fetchGiftCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Transfer Gift Card
      .addCase(transferGiftCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferGiftCard.fulfilled, (state, action) => {
        state.loading = false;
        const code = action.payload?.code;
        if (!code) return;
        const giftCard = state.giftCards.find((gc) => gc.code === code);
        if (giftCard) {
          giftCard.recipient_phone = action.payload.phone_number;
          if (action.payload.recipient_id != null) {
            giftCard.recipient_id = action.payload.recipient_id;
          }
        }
      })
      .addCase(transferGiftCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Redeem Gift Card
      .addCase(redeemGiftCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(redeemGiftCard.fulfilled, (state, action) => {
        state.loading = false;
        // Update gift card status
        const giftCard = state.giftCards.find(gc => gc.code === action.payload.code);
        if (giftCard) {
          giftCard.status = 'REDEEMED';
        }
      })
      .addCase(redeemGiftCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Notifications — use dedicated flag so it doesn't toggle the main spinner
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload;
        state.unreadCount = (action.payload || []).filter((n) => !n.is_read).length;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.notificationsLoading = false;
      })
      // Mark Notification Read
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload.id);
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      // Fetch Status — use dedicated flag
      .addCase(fetchStatus.pending, (state) => {
        state.statusLoading = true;
      })
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.status = normalizeGiftStatus(action.payload);
      })
      .addCase(fetchStatus.rejected, (state) => {
        state.statusLoading = false;
      });
  },
});

export const { clearError } = giftCardsSlice.actions;
export default giftCardsSlice.reducer;
